import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Form, DatePicker, Row, Col, Button, Popover, Input, List, Spin, Typography } from "antd";
import axios from "axios";
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';

const SurveysInfoForm = props => {
  const [form] = Form.useForm();
  const isEditMode = props.isEditMode || false;
  const { t } = useTranslation();

  const PAGE_SIZE = 10;

  // Enterprise popover state
  const [enterprisePopoverOpen, setEnterprisePopoverOpen] = useState(false);
  const [enterpriseSearchValue, setEnterpriseSearchValue] = useState("");
  const [enterpriseSearchResults, setEnterpriseSearchResults] = useState([]);
  const [enterpriseSearchLoading, setEnterpriseSearchLoading] = useState(false);
  const [enterpriseCurrentPage, setEnterpriseCurrentPage] = useState(0);
  const [enterpriseHasMore, setEnterpriseHasMore] = useState(true);
  const [enterpriseLoadingMore, setEnterpriseLoadingMore] = useState(false);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const enterpriseListRef = useRef(null);

  // Event popover state
  const [eventPopoverOpen, setEventPopoverOpen] = useState(false);
  const [eventSearchValue, setEventSearchValue] = useState("");
  const [eventSearchResults, setEventSearchResults] = useState([]);
  const [eventSearchLoading, setEventSearchLoading] = useState(false);
  const [eventCurrentPage, setEventCurrentPage] = useState(0);
  const [eventHasMore, setEventHasMore] = useState(true);
  const [eventLoadingMore, setEventLoadingMore] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const eventListRef = useRef(null);

  // Track if user has manually changed selections (to prevent useEffect from overwriting)
  const enterpriseManuallyChanged = useRef(false);
  const eventManuallyChanged = useRef(false);

  // Initialize selected enterprise from survey data (already includes name/vat)
  useEffect(() => {
    // Don't overwrite user's manual selection
    if (enterpriseManuallyChanged.current) return;

    if (props.record.survey?.enterpriseId) {
      setSelectedEnterprise({
        id: props.record.survey.enterpriseId,
        name: props.record.survey.enterpriseName,
        vat: props.record.survey.enterpriseVat
      });
    } else {
      setSelectedEnterprise(null);
    }
  }, [props.record.survey?.enterpriseId, props.record.survey?.enterpriseName, props.record.survey?.enterpriseVat]);

  // Initialize selected event from survey data (now includes eventName)
  useEffect(() => {
    // Don't overwrite user's manual selection
    if (eventManuallyChanged.current) return;

    if (props.record.survey?.eventId) {
      setSelectedEvent({
        id: props.record.survey.eventId,
        name: props.record.survey.eventName,
        type: props.record.survey.eventTypeDescription,
        date: props.record.survey.eventDate,
        parentEventName: props.record.survey.eventParentName
      });
    } else {
      setSelectedEvent(null);
    }
  }, [props.record.survey?.eventId, props.record.survey?.eventName, props.record.survey?.eventTypeDescription, props.record.survey?.eventDate, props.record.survey?.eventParentName]);

  useEffect(() => {
    if (props.record.survey) {
      form.setFieldsValue({
        createdOn: props.record.survey.createdOn ? dayjs(props.record.survey.createdOn) : null,
        enterprise: props.record.survey.enterpriseId,
        event: props.record.survey.eventId
      });
    } else {
      form.resetFields();
    }
  }, [props.record.survey, form]);

  // Reset validation when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      // Reset manual change flags when exiting edit mode
      enterpriseManuallyChanged.current = false;
      eventManuallyChanged.current = false;

      form.resetFields();

      if (props.record.survey) {
        form.setFieldsValue({
          createdOn: props.record.survey.createdOn ? dayjs(props.record.survey.createdOn) : null,
          enterprise: props.record.survey.enterpriseId,
          event: props.record.survey.eventId
        });
      }
    }
  }, [isEditMode, form, props.record.survey]);

  // Reset flags when switching to a different survey
  useEffect(() => {
    enterpriseManuallyChanged.current = false;
    eventManuallyChanged.current = false;
  }, [props.record.survey?.id]);

  const formItemLayout = {
    labelCol: {
      sm: { span: 4 }
    },
    wrapperCol: {
      sm: { span: 20 }
    }
  };

  // Load initial enterprises (first page)
  const loadInitialEnterprises = useCallback(() => {
    setEnterpriseSearchLoading(true);
    setEnterpriseCurrentPage(0);
    setEnterpriseHasMore(true);
    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/${props.type.apiPath}/`, {
      params: { pageSize: PAGE_SIZE, pageNumber: 1 }
    }).then(({ data }) => {
      setEnterpriseSearchResults(data.data || []);
      setEnterpriseHasMore((data.data || []).length >= PAGE_SIZE);
    }).catch(() => {
      setEnterpriseSearchResults([]);
      setEnterpriseHasMore(false);
    }).finally(() => {
      setEnterpriseSearchLoading(false);
    });
  }, [props.type.name]);

  // Debounced enterprise search
  const debouncedEnterpriseSearch = useMemo(
    () => debounce((value) => {
      if (!value) {
        loadInitialEnterprises();
        return;
      }
      setEnterpriseCurrentPage(0);
      setEnterpriseHasMore(true);
      axios.get(`${process.env.REACT_APP_API_ENDPOINT}/${props.type.apiPath}/`, {
        params: { pageSize: PAGE_SIZE, pageNumber: 1, name: value }
      }).then(({ data }) => {
        setEnterpriseSearchResults(data.data || []);
        setEnterpriseHasMore((data.data || []).length >= PAGE_SIZE);
      }).catch(() => {
        setEnterpriseSearchResults([]);
        setEnterpriseHasMore(false);
      }).finally(() => {
        setEnterpriseSearchLoading(false);
      });
    }, 300),
    [props.type.name, loadInitialEnterprises]
  );

  // Load more enterprises (infinite scroll)
  const loadMoreEnterprises = useCallback(() => {
    if (enterpriseLoadingMore || !enterpriseHasMore) return;

    setEnterpriseLoadingMore(true);
    const nextPage = enterpriseCurrentPage + 1;
    const params = { pageSize: PAGE_SIZE, pageNumber: nextPage + 1 };
    if (enterpriseSearchValue) params.name = enterpriseSearchValue;

    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/${props.type.apiPath}/`, { params })
      .then(({ data }) => {
        setEnterpriseSearchResults(prev => [...prev, ...(data.data || [])]);
        setEnterpriseCurrentPage(nextPage);
        setEnterpriseHasMore((data.data || []).length >= PAGE_SIZE);
      }).catch(() => {
        setEnterpriseHasMore(false);
      }).finally(() => {
        setEnterpriseLoadingMore(false);
      });
  }, [enterpriseLoadingMore, enterpriseHasMore, enterpriseSearchValue, enterpriseCurrentPage, props.type.name]);

  // Load initial events
  const loadInitialEvents = useCallback(() => {
    setEventSearchLoading(true);
    setEventCurrentPage(0);
    setEventHasMore(true);
    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/events/`, {
      params: { page: 0, size: PAGE_SIZE }
    }).then(response => {
      setEventSearchResults(response.data.content || []);
      setEventHasMore(!response.data.last);
    }).catch(() => {
      setEventSearchResults([]);
      setEventHasMore(false);
    }).finally(() => {
      setEventSearchLoading(false);
    });
  }, []);

  // Debounced event search
  const debouncedEventSearch = useMemo(
    () => debounce((value) => {
      if (!value) {
        loadInitialEvents();
        return;
      }
      setEventCurrentPage(0);
      setEventHasMore(true);
      axios.get(`${process.env.REACT_APP_API_ENDPOINT}/events/`, {
        params: { page: 0, size: PAGE_SIZE, name: value }
      }).then(response => {
        setEventSearchResults(response.data.content || []);
        setEventHasMore(!response.data.last);
      }).catch(() => {
        setEventSearchResults([]);
        setEventHasMore(false);
      }).finally(() => {
        setEventSearchLoading(false);
      });
    }, 300),
    [loadInitialEvents]
  );

  // Load more events
  const loadMoreEvents = useCallback(() => {
    if (eventLoadingMore || !eventHasMore) return;

    setEventLoadingMore(true);
    const nextPage = eventCurrentPage + 1;
    const params = { page: nextPage, size: PAGE_SIZE };
    if (eventSearchValue) params.name = eventSearchValue;

    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/events/`, { params })
      .then(response => {
        setEventSearchResults(prev => [...prev, ...(response.data.content || [])]);
        setEventCurrentPage(nextPage);
        setEventHasMore(!response.data.last);
      }).catch(() => {
        setEventHasMore(false);
      }).finally(() => {
        setEventLoadingMore(false);
      });
  }, [eventLoadingMore, eventHasMore, eventSearchValue, eventCurrentPage]);

  // Handle form changes and notify parent
  const handleFormChange = useCallback(() => {
    const values = form.getFieldsValue();

    // Format for parent component
    const formattedFields = {};
    Object.keys(values).forEach(key => {
      formattedFields[key] = { value: values[key] };
    });

    // Only check validation in edit mode
    let isValid = true;
    if (isEditMode) {
      // Check for existing errors without triggering new validation
      const errors = form.getFieldsError();
      const hasErrors = errors.some(error => error.errors.length > 0);

      // Check for required empty fields
      const requiredFields = ['createdOn', 'enterprise', 'event'];
      const hasEmptyRequired = requiredFields.some(field =>
        !values[field]
      );

      isValid = !hasErrors && !hasEmptyRequired;
    }

    // Notify parent
    props.parentCallback(formattedFields, isValid);
  }, [form, isEditMode, props]);

  // Enterprise handlers
  const handleEnterprisePopoverOpenChange = useCallback((open) => {
    setEnterprisePopoverOpen(open);
    if (open) {
      setEnterpriseSearchValue("");
      loadInitialEnterprises();
    }
  }, [loadInitialEnterprises]);

  const handleEnterpriseSearchChange = useCallback((e) => {
    const value = e.target.value;
    setEnterpriseSearchValue(value);
    setEnterpriseSearchLoading(true);
    debouncedEnterpriseSearch(value);
  }, [debouncedEnterpriseSearch]);

  const handleEnterpriseScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreEnterprises();
    }
  }, [loadMoreEnterprises]);

  const handleSelectEnterprise = useCallback((enterprise) => {
    enterpriseManuallyChanged.current = true;  // Mark as manually changed
    setSelectedEnterprise(enterprise);
    form.setFieldValue('enterprise', enterprise.id);
    setEnterprisePopoverOpen(false);
    setEnterpriseSearchValue("");
    handleFormChange();
  }, [form, handleFormChange]);

  // Event handlers
  const handleEventPopoverOpenChange = useCallback((open) => {
    setEventPopoverOpen(open);
    if (open) {
      setEventSearchValue("");
      loadInitialEvents();
    }
  }, [loadInitialEvents]);

  const handleEventSearchChange = useCallback((e) => {
    const value = e.target.value;
    setEventSearchValue(value);
    setEventSearchLoading(true);
    debouncedEventSearch(value);
  }, [debouncedEventSearch]);

  const handleEventScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      loadMoreEvents();
    }
  }, [loadMoreEvents]);

  const handleSelectEvent = useCallback((event) => {
    eventManuallyChanged.current = true;  // Mark as manually changed
    setSelectedEvent(event);
    form.setFieldValue('event', event.id);
    setEventPopoverOpen(false);
    setEventSearchValue("");
    handleFormChange();
  }, [form, handleFormChange]);

  const enterprisePopoverContent = (
    <div style={{ width: 350 }}>
      <Input.Search
        placeholder="Search by name..."
        value={enterpriseSearchValue}
        onChange={handleEnterpriseSearchChange}
        loading={enterpriseSearchLoading}
        style={{ marginBottom: 12 }}
        allowClear
      />
      <Spin spinning={enterpriseSearchLoading}>
        <div ref={enterpriseListRef} onScroll={handleEnterpriseScroll} style={{ maxHeight: 250, overflowY: 'auto' }}>
          <List
            size="small"
            dataSource={enterpriseSearchResults}
            locale={{ emptyText: 'No enterprises found' }}
            renderItem={(enterprise) => (
              <List.Item
                key={enterprise.id}
                onClick={() => handleSelectEnterprise(enterprise)}
                style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: 4 }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{enterprise.name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{enterprise.vat || 'No VAT'}</div>
                </div>
              </List.Item>
            )}
          />
          {enterpriseLoadingMore && <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>}
          {!enterpriseHasMore && enterpriseSearchResults.length > 0 && (
            <div style={{ textAlign: 'center', padding: 8, color: '#888', fontSize: 12 }}>No more enterprises</div>
          )}
        </div>
      </Spin>
    </div>
  );

  const eventPopoverContent = (
    <div style={{ width: 400 }}>
      <Input.Search
        placeholder="Search by name..."
        value={eventSearchValue}
        onChange={handleEventSearchChange}
        loading={eventSearchLoading}
        style={{ marginBottom: 12 }}
        allowClear
      />
      <Spin spinning={eventSearchLoading}>
        <div ref={eventListRef} onScroll={handleEventScroll} style={{ maxHeight: 250, overflowY: 'auto' }}>
          <List
            size="small"
            dataSource={eventSearchResults}
            locale={{ emptyText: 'No events found' }}
            renderItem={(event) => (
              <List.Item
                key={event.id}
                onClick={() => handleSelectEvent(event)}
                style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: 4 }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{event.name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {t(`events.type.${event.type}`)} - {event.date ? new Date(event.date).toLocaleDateString() : 'No date'}
                  </div>
                </div>
              </List.Item>
            )}
          />
          {eventLoadingMore && <div style={{ textAlign: 'center', padding: 8 }}><Spin size="small" /></div>}
          {!eventHasMore && eventSearchResults.length > 0 && (
            <div style={{ textAlign: 'center', padding: 8, color: '#888', fontSize: 12 }}>No more events</div>
          )}
        </div>
      </Spin>
    </div>
  );

  return (
    <>
      <Form
        form={form}
        {...formItemLayout}
        onValuesChange={handleFormChange}
        onFieldsChange={handleFormChange}
      >
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="Date of the survey"
              name="createdOn"
              rules={isEditMode ? [{ required: true, message: "Please input Date!" }] : []}
            >
              <DatePicker disabled={!isEditMode} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item
              label="Enterprise"
              name="enterprise"
              labelCol={{ span: 4 }}
              rules={isEditMode ? [{ required: true, message: 'Please select an enterprise!' }] : []}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Typography.Text style={{ flex: 1 }}>
                  {selectedEnterprise ? `${selectedEnterprise.name} (${selectedEnterprise.vat || 'No VAT'})` : 'No enterprise selected'}
                </Typography.Text>
                {isEditMode && (
                  <Popover
                    content={enterprisePopoverContent}
                    title="Select Enterprise"
                    trigger="click"
                    open={enterprisePopoverOpen}
                    onOpenChange={handleEnterprisePopoverOpenChange}
                    placement="bottomRight"
                  >
                    <Button type="primary" size="small">Select...</Button>
                  </Popover>
                )}
              </div>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Event"
              name="event"
              labelCol={{ span: 4 }}
              rules={isEditMode ? [{ required: true, message: 'Please select an event!' }] : []}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <Typography.Text>
                    {selectedEvent
                      ? `${selectedEvent.name} (${t(`events.type.${selectedEvent.type}`)} - ${selectedEvent.date ? new Date(selectedEvent.date).toLocaleDateString() : 'No date'})`
                      : 'No event selected'}
                  </Typography.Text>
                  {selectedEvent && selectedEvent.type === 'MULTI_HAZARD_EVENT' && (
                    <div style={{ fontSize: 12, color: '#fa8c16' }}>Multi-hazard event</div>
                  )}
                  {selectedEvent && selectedEvent.type !== 'MULTI_HAZARD_EVENT' && selectedEvent.parentEventName && (
                    <div style={{ fontSize: 12, color: '#fa8c16' }}>
                      Part of multi-hazard: {selectedEvent.parentEventName}
                    </div>
                  )}
                </div>
                {isEditMode && (
                  <Popover
                    content={eventPopoverContent}
                    title="Select Event"
                    trigger="click"
                    open={eventPopoverOpen}
                    onOpenChange={handleEventPopoverOpenChange}
                    placement="bottomRight"
                  >
                    <Button type="primary" size="small">Select...</Button>
                  </Popover>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default SurveysInfoForm;
